import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import api, { fmtIDR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Upload, MessageSquare } from "lucide-react";
import { DeliveryStatusBanner } from "../components/DeliveryStatusBanner";
import { RefundBanner } from "../components/HeaderBalance";
import { OrderItems, PartialDeliveryForm } from "../components/OrderExtras";
import PaymentQR from "../components/PaymentQR";
import PayPalPanel from "../components/PayPalPanel";
import BinancePayAutoPanel from "../components/BinancePayAutoPanel";

const STATUS_TABS = ["all", "waiting_payment", "verifying", "preparing", "shipped", "completed", "cancelled", "resolution"];

export function OrdersList() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");

  const load = () => api.get("/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const filtered = tab === "all" ? orders : orders.filter(o => o.status === tab);
  const statusColor = (s) => ({
    waiting_payment: "text-[#A8A8AD] border-[#35353B]",
    verifying: "text-[#D4AF37] border-[#D4AF37]",
    preparing: "text-[#D4AF37] border-[#D4AF37]",
    shipped: "text-[#35C78A] border-[#35C78A]",
    completed: "text-[#35C78A] border-[#35C78A]",
    cancelled: "text-[#F06464] border-[#F06464]",
    resolution: "text-[#F06464] border-[#F06464]",
  }[s] || "text-[#A8A8AD] border-[#35353B]");

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <h1 className="text-3xl font-bold text-[#F5F3ED] mb-6" style={{fontFamily:"'Playfair Display', serif"}}>
          {user.role === "buyer" ? t("order.my_orders") : t("order.sales")}
        </h1>
        <div className="flex gap-1 flex-wrap mb-6 border-b border-[#35353B]">
          {STATUS_TABS.map(s => (
            <button key={s} data-testid={`orders-tab-${s}`} onClick={()=>setTab(s)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${tab===s ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A8A8AD] hover:text-[#F5F3ED]"}`}>
              {s === "all" ? t("order.all") : t(`order.status.${s}`)}
            </button>
          ))}
        </div>

        <div className="bg-[#202127] border border-[#35353B] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#191A1F] border-b border-[#35353B]">
              <tr className="text-left text-xs uppercase tracking-widest text-[#A8A8AD]">
                <th className="px-4 py-3">{t("order.no")}</th>
                <th className="px-4 py-3">{t("order.date")}</th>
                <th className="px-4 py-3">{t("order.items")}</th>
                <th className="px-4 py-3 text-right">{t("checkout.total")}</th>
                <th className="px-4 py-3">{t("order.status")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-[#35353B] hover:bg-[#191A1F] transition-colors">
                  <td className="px-4 py-3 font-mono text-[#D4AF37]">{o.order_no}</td>
                  <td className="px-4 py-3 text-[#A8A8AD]">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-[#F5F3ED]">{o.items.map(i => i.product_name).join(", ")}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#D4AF37]">{fmtIDR(o.grand_total_idr ?? o.total_idr)}</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-1 border rounded-sm text-xs ${statusColor(o.status)}`}>{t(`order.status.${o.status}`)}</span>
                    {o.status === "preparing" && o.trade_ready && <span data-testid={`order-trade-ready-badge-${o.id}`} className="ml-2 inline-block px-2 py-1 rounded-sm text-xs bg-[#D4AF37] text-[#101114] font-bold">{t("order.trade_ready_badge")}</span>}</td>
                  <td className="px-4 py-3"><Link to={`/orders/${o.id}`} data-testid={`view-order-${o.id}`} className="text-[#D4AF37] hover:underline">{t("order.detail")}</Link></td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-[#A8A8AD]">{t("common.empty")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [proof, setProof] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const [video, setVideo] = useState("");
  const [opening, setOpening] = useState(false);

  const load = () => api.get(`/orders/${id}`).then(r => setO(r.data));
  useEffect(() => { load(); }, [id]);

  const handleFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error(t("order.max_5mb")); return; }
    const reader = new FileReader();
    reader.onload = () => setProof(reader.result);
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!proof) return;
    setUploading(true);
    try {
      await api.post(`/orders/${id}/payment-proof`, { image_b64: proof });
      toast.success(t("order.proof_sent"));
      setProof(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("order.upload_failed"));
    } finally { setUploading(false); }
  };

  const changeStatus = async (status) => {
    if (status === "cancelled" && !window.confirm("Batalkan pesanan ini? Nilai item yang belum terkirim akan masuk ke Saldo Store pembeli.")) return;
    if (status === "shipped" && !/^https?:\/\/\S+$/.test(video.trim())) { toast.error("URL video bukti pengiriman wajib diisi (http/https)"); return; }
    try {
      await api.patch(`/orders/${id}/status`, { status, note, ...(status === "shipped" ? { video_url: video.trim() } : {}) });
      toast.success(`Status diubah: ${t(`order.status.${status}`)}`);
      setNote(""); setVideo(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const tradeReady = async () => {
    try {
      await api.post(`/orders/${id}/trade-ready`, { note });
      toast.success("Pembeli diberi tahu: staff siap trade");
      setNote(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  if (!o) return <div className="min-h-screen bg-[#101114]"><Header /><div className="p-10 text-[#A8A8AD]">{t("common.loading")}</div></div>;

  const canStaff = user && (user.role === "owner" || user.role === "staff");
  const messageBuyer = async () => {
    setOpening(true);
    try {
      const r = await api.post("/conversations", { peer_id: o.buyer_id, order_id: o.id });
      navigate(`/chat?c=${r.data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Tidak bisa membuka percakapan"); setOpening(false); }
  };
  const chatStaff = async () => {
    setOpening(true);
    try {
      const r = await api.post(`/orders/${o.id}/support-chat`);
      navigate(`/chat?c=${r.data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || t("order.chat_failed")); setOpening(false); }
  };
  const nextActions = {
    waiting_payment: canStaff ? [["cancelled", "Batalkan"]] : [],
    verifying: canStaff ? [["preparing", "Verifikasi & Siapkan"], ["cancelled", "Tolak"]] : [],
    preparing: canStaff ? [["shipped", "Catat Pengiriman (semua terkirim)"], ["cancelled", "Batalkan & refund sisa ke Saldo Store"]] : [],
    shipped: canStaff ? [["completed", "Selesaikan"]] : [],
  }[o.status] || [];

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
        <button onClick={()=>navigate(-1)} className="text-sm text-[#A8A8AD] hover:text-[#D4AF37] mb-4">{t("order.back")}</button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37]">{t("order.no")}</div>
            <h1 className="text-2xl font-bold text-[#F5F3ED] font-mono">{o.order_no}</h1>
          </div>
          <div className="flex items-center gap-2">
            {canStaff && (
              <button data-testid="message-buyer-btn" onClick={messageBuyer} disabled={opening}
                className="flex items-center gap-2 border border-[#35353B] text-[#F5F3ED] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors rounded-sm px-3 py-1 text-sm disabled:opacity-50">
                <MessageSquare size={15} /> Pesan Buyer
              </button>
            )}
            {user.role === "buyer" && (
              <button data-testid="chat-staff-btn" onClick={chatStaff} disabled={opening}
                className="flex items-center gap-2 border border-[#35353B] text-[#F5F3ED] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors rounded-sm px-3 py-1 text-sm disabled:opacity-50">
                <MessageSquare size={15} /> {t("order.chat_staff")}
              </button>
            )}
            <span className="px-3 py-1 border border-[#D4AF37] text-[#D4AF37] rounded-sm text-sm">{t(`order.status.${o.status}`)}</span>
          </div>
        </div>

        {user.role === "buyer" && <RefundBanner o={o} />}
        <DeliveryStatusBanner o={o} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <OrderItems o={o} />

            {o.payment_proof && (
              <div className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
                <div className="text-xs uppercase tracking-widest text-[#A8A8AD] mb-3">{t("order.payment_proof")}</div>
                <img src={o.payment_proof.image_b64} alt="proof" className="max-w-xs rounded-sm" />
              </div>
            )}

            <div className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
              <div className="text-xs uppercase tracking-widest text-[#A8A8AD] mb-3">{t("order.history")}</div>
              {o.history.map((h, i) => (
                <div key={i} className="flex gap-3 py-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-1.5" />
                  <div className="flex-1">
                    <div className="text-[#F5F3ED]">{t(`order.status.${h.status}`)}</div>
                    <div className="text-xs text-[#A8A8AD]">{new Date(h.at).toLocaleString()} · {h.by}{h.note ? ` — ${h.note}` : ""}</div>
                    {h.video_url && <a href={h.video_url} target="_blank" rel="noreferrer" data-testid={`history-video-${i}`} className="text-xs text-[#D4AF37] hover:underline break-all">▶ {t("order.watch_video")}</a>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
              <div className="text-xs uppercase tracking-widest text-[#A8A8AD] mb-2">{t("order.payment")}</div>
              <div className="text-[#F5F3ED] mb-1">{o.payment_method}{o.payment_asset === "USDT" ? ` (USDT${o.payment_network ? ` ${o.payment_network}` : ""})` : o.currency === "USD" ? " (USD)" : ""}</div>
              {o.payment_details && !["crypto", "binance_pay"].includes(o.payment_type) && <div data-testid="order-payment-details" className="font-mono text-sm text-[#D4AF37] mb-2 whitespace-pre-line">{o.payment_details}</div>}
              {o.payment_type === "paypal" && <PayPalPanel o={o} isBuyer={user.role === "buyer"} reload={load} />}
              {o.payment_type === "binance_pay_auto" && <BinancePayAutoPanel o={o} isBuyer={user.role === "buyer"} reload={load} />}
              {o.status === "waiting_payment" && <PaymentQR o={o} onExpire={load} />}
              {o.delivery_notes && <div className="text-xs text-[#A8A8AD]">{o.delivery_notes}</div>}
            </div>

            {o.delivery && (
              <div data-testid="order-delivery-info" className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
                <div className="text-xs uppercase tracking-widest text-[#A8A8AD] mb-2">{t("order.delivery")}</div>
                <div className="text-[#D4AF37] font-semibold mb-1">{t(`delivery.${o.delivery.method}`)}</div>
                {o.delivery.character_name && <div className="text-sm text-[#F5F3ED]">{t("order.character")}: <span data-testid="order-delivery-character" className="font-mono">{o.delivery.character_name}</span></div>}
                {o.delivery.notes && <div className="text-xs text-[#A8A8AD] mt-1 whitespace-pre-wrap">{o.delivery.notes}</div>}
              </div>
            )}

            {user.role === "buyer" && o.status === "waiting_payment" && !o.paypal_auto && o.payment_type !== "binance_pay_auto" && (
              <div className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3">{t("order.upload_proof")}</div>
                <input data-testid="proof-file-input" type="file" accept="image/*" onChange={handleFile} className="text-xs text-[#A8A8AD] mb-3 w-full" />
                {proof && <img src={proof} alt="preview" className="max-w-full rounded-sm mb-3" />}
                <button data-testid="upload-proof-btn" onClick={upload} disabled={!proof || uploading} className="w-full bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-50 font-bold rounded-sm py-2 flex items-center justify-center gap-2">
                  <Upload size={16} /> {uploading ? "..." : t("order.send")}
                </button>
              </div>
            )}

            {canStaff && nextActions.length > 0 && (
              <div className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Tindakan</div>
                <div className="space-y-2">
                  <textarea data-testid="action-note-input" value={note} maxLength={500} onChange={(e)=>setNote(e.target.value)} rows={2}
                    placeholder="Catatan untuk pembeli (opsional), mis. nama karakter staff / lokasi trade"
                    className="w-full bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
                  {o.status === "preparing" && (
                    <input data-testid="action-video-url-input" type="url" value={video} maxLength={500} onChange={(e)=>setVideo(e.target.value)}
                      placeholder="URL video bukti (wajib untuk Catat Pengiriman)"
                      className="w-full bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
                  )}
                  {o.status === "preparing" && (
                    <button data-testid="action-trade-ready-btn" onClick={tradeReady} className="w-full bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] font-bold rounded-sm py-2 text-sm transition-colors">
                      {o.trade_ready ? "Kirim Ulang: Siap Trade Sekarang" : "Siap Trade Sekarang"}
                    </button>
                  )}
                  {nextActions.map(([s, label]) => (
                    <button key={s} data-testid={`action-${s}-btn`} onClick={()=>changeStatus(s)} className="w-full border border-[#35353B] hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#F5F3ED] rounded-sm py-2 text-sm transition-colors">
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {canStaff && o.status === "preparing" && <PartialDeliveryForm o={o} onDone={load} />}
          </div>
        </div>
      </div>
    </div>
  );
}
